import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  Table,
  Td,
  Th,
} from '@/components/ui'
import { useDebounced, useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { relativeTime } from '@/lib/format'
import type { Ack, ContactListItem, Page } from '@/types'

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'new', label: 'New' },
  { value: 'queued', label: 'Queued' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'profiled', label: 'Profiled' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'opted_out', label: 'Opted out' },
]

const SORT_OPTIONS = [
  { value: 'readiness', label: 'Sort: opportunity readiness' },
  { value: 'opportunities', label: 'Sort: open opportunities' },
  { value: 'recent', label: 'Sort: recently updated' },
  { value: 'name', label: 'Sort: name' },
]

const PAGE_SIZE = 50

export function ContactsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState('readiness')
  const [offset, setOffset] = useState(0)
  const [importOpen, setImportOpen] = useState(false)

  const debouncedSearch = useDebounced(search)

  const query = useQuery(
    useCallback(
      () =>
        api.get<Page<ContactListItem>>('/contacts', {
          q: debouncedSearch || undefined,
          status: status || undefined,
          sort,
          limit: PAGE_SIZE,
          offset,
        }),
      [debouncedSearch, status, sort, offset],
    ),
    [debouncedSearch, status, sort, offset],
  )

  const page = query.data

  return (
    <>
      <PageHeader
        title="Contacts"
        subtitle="Ranked by how ready they are for an introduction, not by how complete their profile is."
        action={
          <button type="button" className="btn-primary" onClick={() => setImportOpen(true)}>
            Import contacts
          </button>
        }
      />

      {/* Filters sit in one row above the data. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="field w-auto min-w-[16rem] flex-1 py-1.5 text-sm"
          placeholder="Search name, profession, company, services…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setOffset(0)
          }}
          aria-label="Search contacts"
        />
        <Select
          label="Status"
          value={status}
          onChange={(value) => {
            setStatus(value)
            setOffset(0)
          }}
          options={STATUS_OPTIONS}
        />
        <Select label="Sort" value={sort} onChange={setSort} options={SORT_OPTIONS} />
      </div>

      <Card>
        {query.loading && !page ? (
          <Spinner />
        ) : query.error ? (
          <ErrorNote message={query.error} onRetry={query.reload} />
        ) : !page || page.items.length === 0 ? (
          <EmptyState
            title="No contacts match this view"
            hint="Import a list to start building the network, or clear the filters above."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Contact</Th>
                  <Th>Status</Th>
                  <Th>Readiness</Th>
                  <Th numeric>Opportunities</Th>
                  <Th numeric>Matches</Th>
                  <Th>Last reply</Th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((contact) => (
                  <tr key={contact.id} className="hover:bg-raised">
                    <Td>
                      <Link
                        to={`/contacts/${contact.id}`}
                        className="flex items-center gap-2.5 hover:underline"
                      >
                        <Avatar name={contact.name} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-ink">
                            {contact.name}
                          </span>
                          <span className="block truncate text-xs text-ink-muted">
                            {contact.headline || contact.phone_e164}
                          </span>
                        </span>
                      </Link>
                    </Td>
                    <Td>
                      <StatusBadge status={contact.status} />
                    </Td>
                    <Td>
                      <ReadinessCell
                        readiness={contact.opportunity_readiness}
                        completeness={contact.profile_completeness}
                      />
                    </Td>
                    <Td numeric>
                      {contact.opportunity_count > 0 ? (
                        <span className="font-medium text-ink">{contact.opportunity_count}</span>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Td>
                    <Td numeric>
                      {contact.match_count || <span className="text-ink-faint">—</span>}
                    </Td>
                    <Td>
                      <span className="text-xs text-ink-muted">
                        {relativeTime(contact.last_conversation_at || contact.last_inbound_at)}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Pagination
              total={page.total}
              offset={offset}
              limit={PAGE_SIZE}
              count={page.items.length}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false)
          query.reload()
        }}
      />
    </>
  )
}

function ReadinessCell({
  readiness,
  completeness,
}: {
  readiness: number
  completeness: number
}) {
  return (
    <div className="min-w-[7rem]">
      <div
        className="h-1.5 w-full overflow-hidden rounded-full"
        style={{ background: 'var(--wash)' }}
        role="meter"
        aria-valuenow={Math.round(readiness * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Opportunity readiness"
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(readiness * 100, readiness > 0 ? 3 : 0)}%`,
            background: 'var(--series-1)',
          }}
        />
      </div>
      <p className="mt-1 text-xs text-ink-faint">
        <span className="tabular">{Math.round(readiness * 100)}%</span> ready ·{' '}
        <span className="tabular">{Math.round(completeness * 100)}%</span> complete
      </p>
    </div>
  )
}

function Pagination({
  total,
  offset,
  limit,
  count,
  onChange,
}: {
  total: number
  offset: number
  limit: number
  count: number
  onChange: (offset: number) => void
}) {
  const from = total === 0 ? 0 : offset + 1
  const to = offset + count
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <p className="text-xs text-ink-muted">
        <span className="tabular">
          {from}–{to}
        </span>{' '}
        of <span className="tabular">{total}</span>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-secondary px-2.5 py-1 text-xs"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn-secondary px-2.5 py-1 text-xs"
          disabled={to >= total}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

function ImportModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean
  onClose: () => void
  onImported: () => void
}) {
  const [raw, setRaw] = useState('')
  const [consent, setConsent] = useState(false)
  const [outreach, setOutreach] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  async function handleImport() {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const contacts = parseContacts(raw)
      if (!contacts.length) throw new Error('No valid rows were found.')

      const ack = await api.post<Ack>('/contacts/import', {
        contacts,
        start_outreach: outreach,
        consent_confirmed: consent,
      })
      setResult(ack.detail ?? 'Imported.')
      setRaw('')
      onImported()
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Import failed.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title="Import contacts"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleImport}
            disabled={busy || !raw.trim() || (outreach && !consent)}
          >
            {busy ? 'Importing…' : 'Import'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Contacts"
          hint="One per line: phone, name. The phone must be E.164, e.g. +447700900123."
        >
          <textarea
            className="field h-40 font-mono text-xs"
            value={raw}
            onChange={(event) => setRaw(event.target.value)}
            placeholder={'+447700900123, Sarah Okonkwo\n+971500000000, Ahmed Rahman'}
          />
        </Field>

        <label className="flex items-start gap-2.5 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
          />
          <span>
            I have a lawful basis for messaging these people.
            <span className="mt-0.5 block text-xs text-ink-muted">
              Recorded against each contact as an outreach consent record.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={outreach}
            disabled={!consent}
            onChange={(event) => setOutreach(event.target.checked)}
          />
          <span>
            Start the first conversation now
            <span className="mt-0.5 block text-xs text-ink-muted">
              Sends are staggered and capped daily. Requires the confirmation above.
            </span>
          </span>
        </label>

        {error && (
          <p className="text-sm" style={{ color: 'var(--critical)' }} role="alert">
            {error}
          </p>
        )}
        {result && (
          <p className="text-sm" style={{ color: 'var(--good)' }}>
            {result}
          </p>
        )}

        <Badge tone="info">
          Duplicates are skipped automatically — importing the same list twice is safe.
        </Badge>
      </div>
    </Modal>
  )
}

function parseContacts(raw: string): Array<{ phone: string; display_name?: string }> {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [phone, ...rest] = line.split(/[,;\t]/).map((part) => part.trim())
      const name = rest.join(' ').trim()
      return name ? { phone, display_name: name } : { phone }
    })
    .filter((entry) => /^\+?[1-9]\d{6,14}$/.test(entry.phone.replace(/[\s\-().]/g, '')))
}
